<script setup>
  import Header from '@/components/Header.vue'
  import Footer from '@/components/Footer.vue'
  import { ref, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import { useDialogStore } from '@/stores/dialogStore'
  import { useContractStore } from '@/stores/contractStore'
  import { useUserStore } from '@/stores/userStore'

  const router = useRouter()
  const dialogStore = useDialogStore()
  const contractStore = useContractStore()
  const userStore = useUserStore()

  const breadcrumbs = [{ name: 'ダッシュボード', url: 'dashboard' }, { name: '契約確認', url: 'contract' }, { name: '解約手続き' }]

  // methods ///////////////////////////////////////////////////////////////

  onMounted(async () => {
    // ユーザー情報を取得
    await userStore.getCurrentUser()

    // 権限チェック（rootユーザーのみ許可）
    if (!userStore.isRoot()) {
      await dialogStore.showAlertDialog('権限エラー', 'この機能にアクセスする権限がありません。')
      router.push('/dashboard')
      return
    }

    // 現在の契約情報を取得
    await contractStore.getCurrentContract()

    // 有料プランでない場合は契約確認画面に戻る
    if (!contractStore.isPaidStatus) {
      await dialogStore.showAlertDialog('エラー', '有料プランを契約していません。')
      router.push('/contract')
      return
    }
  })

  // 解約処理
  const onCancel = async () => {
    // 最終確認ダイアログを表示
    const confirmed = await dialogStore.showConfirmDialog('解約確認', '本当に解約しますか？')

    if (confirmed) {
      // 解約処理を実行
      const result = await contractStore.cancelPlan()
      if (result) {
        await dialogStore.showMessageDialog('解約完了', result)
        // 契約確認画面に戻る
        router.push('/contract')
      }
    }
  }

  // 戻るボタン
  const onBack = () => {
    router.push('/contract')
  }
</script>

<template>
  <Header />
  <main class="app-main">
    <div class="main-header">
      <button type="button" class="back" @click="onBack">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h2>解約手続き</h2>
    </div>
    <div class="main-body">
      <div class="cancellation-content">
        <!-- 解約についての説明 -->
        <section class="cancellation-warning">
          <div class="warning-icon">
            <span class="material-symbols-outlined">warning</span>
          </div>
          <h3>解約をご検討の前に</h3>
          <p>解約手続きを行う前に、下記の解約ルールをご確認ください。</p>
        </section>

        <!-- 解約ルールの説明 -->
        <section class="cancellation-rules">
          <h3>解約ルール</h3>
          <ul class="rules-list">
            <li>解約した月の末日の締めを以て最後の請求となります。</li>
            <li>解約後、ご契約の期間満了時に自動的に無料プランに移行されます。</li>
            <li>契約途中での解約の場合でも返金は致しません。期間満了までシステムをご利用頂けます。</li>
            <li>無料プランでは、新しい情報の入力は出来ませんが、これまで入力した情報の閲覧は可能です。</li>
          </ul>
        </section>

        <!-- 解約ボタン -->
        <section class="cancellation-action">
          <div class="action-container">
            <p class="final-warning">上記の内容をご理解いただいた上で、解約手続きを行う場合は下記のボタンをクリックしてください。</p>
            <button class="btn btn-danger" @click="onCancel">解約する</button>
          </div>
        </section>
      </div>
    </div>
  </main>
  <Footer :breadcrumbs="breadcrumbs" />
</template>

<style lang="less" scoped>
  .cancellation-content {
    margin: 0 auto;
    padding: 20px 60px;
    width: 100%;
  }

  .main-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    h2 {
      flex-grow: 1;
      margin: 0;
      margin-left: 10px;
      color: #dc3545;
    }
  }

  .material-symbols-outlined {
    vertical-align: middle;
  }

  section {
    margin-bottom: 40px;

    h3 {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 16px;
      color: #333;
      border-bottom: 2px solid #dc3545;
      padding-bottom: 8px;
    }
  }

  .cancellation-warning {
    background-color: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 8px;
    padding: 30px;
    text-align: center;
    max-width: 800px;
    margin: 0 auto 10px;

    @media (min-width: 1200px) {
      max-width: 900px;
    }

    @media (min-width: 1600px) {
      max-width: 1000px;
    }

    @media (max-width: 768px) {
      max-width: 400px;
    }

    .warning-icon {
      margin-bottom: 16px;

      .material-symbols-outlined {
        font-size: 48px;
        color: #ff9800;
      }
    }

    h3 {
      border: none;
      color: #856404;
      margin-bottom: 16px;
    }

    p {
      color: #856404;
      font-size: 16px;
      margin: 0;
      line-height: 1.6;
    }
  }

  .rules-list {
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    border-radius: 8px;
    padding: 20px;
    margin: 0 auto;
    max-width: 800px;

    @media (min-width: 1200px) {
      max-width: 900px;
    }

    @media (min-width: 1600px) {
      max-width: 1000px;
    }

    @media (max-width: 768px) {
      max-width: 400px;
    }

    li {
      margin-bottom: 12px;
      line-height: 1.6;
      font-size: 14px;
      color: #721c24;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .cancellation-action {
    .action-container {
      background-color: #fff;
      border: 2px solid #dc3545;
      border-radius: 12px;
      padding: 40px;
      text-align: center;
      max-width: 600px;
      margin: 0 auto;

      @media (min-width: 1200px) {
        max-width: 700px;
      }

      @media (min-width: 1600px) {
        max-width: 800px;
      }

      @media (max-width: 768px) {
        max-width: 400px;
      }

      .final-warning {
        font-size: 16px;
        color: #495057;
        margin-bottom: 30px;
        line-height: 1.6;
      }
    }
  }

  .btn {
    border: none;
    border-radius: 8px;
    padding: 16px 32px;
    font-size: 18px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s ease;

    &.btn-danger {
      background-color: #dc3545;
      color: #fff;

      &:hover {
        background-color: #c82333;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  .back {
    background: none;
    border: none;
    padding: 8px;
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.3s;

    &:hover {
      background-color: #f8f9fa;
    }

    .material-symbols-outlined {
      font-size: 24px;
      color: #6c757d;
    }
  }
</style>
